/**
 * PAX — PWA Service Worker Registration
 *
 * Rejestruje SW TYLKO w produkcji, obsługuje aktualizacje via workbox-window,
 * wystawia globalny event `pax:sw-update-available` przez którego UI może
 * zaproponować użytkownikowi przeładowanie.
 *
 * Cykl życia:
 *  1. Load → register('/service-worker.js')
 *  2. workbox.addEventListener('waiting') → nowy SW czeka, emit update event
 *  3. UI reaguje: pokazuje toast "Zaktualizuj" (PwaUpdateToast) → user klika
 *  4. workbox.messageSkipWaiting() → SW aktywuje się natychmiast
 *  5. workbox.addEventListener('controlling') → window.location.reload()
 *
 * Nie używamy w DEV (NODE_ENV !== 'production'):
 *  - SW nie jest wtedy budowany przez InjectManifest
 *  - unikamy cache'owania kodu devowego które utrudnia debug
 */
import { Workbox } from 'workbox-window';

let wb = null;

export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  // Sprawdzamy że nie jesteśmy w środowisku Emergent Visual Edit iframe
  // (SW zarejestrowany w iframe może kolidować z hostem)
  if (window.self !== window.top) return;

  window.addEventListener('load', () => {
    wb = new Workbox('/service-worker.js', { scope: '/' });

    // Nowy SW zainstalowany, czeka na aktywację (stary SW nadal kontroluje strony)
    wb.addEventListener('waiting', () => {
      window.dispatchEvent(
        new CustomEvent('pax:sw-update-available', { detail: { workbox: wb } })
      );
    });

    // Nowy SW przejął kontrolę → wymuszamy reload aby użytkownik dostał świeży bundle
    wb.addEventListener('controlling', () => {
      // Chronimy przed zapętleniem reloadów w devtoolsie
      if (!window.__paxReloadInProgress) {
        window.__paxReloadInProgress = true;
        window.location.reload();
      }
    });

    wb.addEventListener('activated', (event) => {
      // Pierwsza aktywacja (nie update) — brak potrzeby reloadu
      if (!event.isUpdate) {
        // Można wysłać metrykę do PostHog jeśli chcemy tracking pierwszej instalacji SW
      }
    });

    wb.register().catch((err) => {
      // Świadome: nie logujemy do PostHog żadnych danych z SW registration
      // eslint-disable-next-line no-console
      console.warn('[PAX PWA] Rejestracja Service Workera nie powiodła się:', err);
    });
  });
}

/**
 * Wywołuje `SKIP_WAITING` w oczekującym SW — zwykle w reakcji na kliknięcie
 * "Zaktualizuj" przez użytkownika. Po tym SW przejmuje kontrolę i wystrzeliwuje
 * event `controlling` który powoduje reload.
 */
export function activateWaitingServiceWorker() {
  if (wb) {
    wb.messageSkipWaiting();
  }
}

/**
 * Ręczne sprawdzenie dostępności nowej wersji SW (kliknięcie w kafelek
 * "Zainstaluj aplikację" w Ustawieniach).
 *
 * Wykorzystuje istniejący mechanizm: `wb.update()` (workbox) → jeżeli backend
 * ma nowy SW, workbox wystrzeli event `waiting` który złapie już istniejący
 * listener w `registerServiceWorker()` i wyemituje `pax:sw-update-available`.
 *
 * NIE tworzymy równoległego systemu aktualizacji — funkcja jest cienkim
 * wrapperem nad `Workbox.update()`.
 *
 * Zwraca Promise, który resolwuje się gdy workbox zakończy próbę.
 * W DEV / bez SW (np. preview iframe) → resolve natychmiast (no-op).
 */
export async function checkForServiceWorkerUpdate() {
  if (wb) {
    try {
      await wb.update();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err };
    }
  }
  // Fallback: natywna ścieżka gdy workbox z jakiegoś powodu nie zarejestrowany,
  // ale SW istnieje (np. rejestracja z innej karty).
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.update();
        return { ok: true };
      }
    } catch (err) {
      return { ok: false, error: err };
    }
  }
  return { ok: false, error: new Error('service-worker-unavailable') };
}
